import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowRight,
  Search,
  Pencil,
  Trash2,
  Users,
  Building2,
  AlertOctagon,
  Clock,
  Loader2,
  Upload,
} from "lucide-react";
import { api } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { companyTypeLabel } from "@/lib/companyTypes";
import { formatDueDate } from "@/lib/status";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/auth";
import AppHeader from "@/components/AppHeader";
import AccountStatusBadge from "@/components/AccountStatusBadge";
import ClientFormModal from "@/components/ClientFormModal";
import SettingsModal from "@/components/SettingsModal";
import ImportClientsModal from "@/components/ImportClientsModal";
import MasterLibraryModal from "@/components/MasterLibraryModal";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";
import BulkActionsBar from "@/components/BulkActionsBar";
import BulkDeleteConfirmDialog from "@/components/BulkDeleteConfirmDialog";
import { Skeleton } from "@/components/ui/skeleton";

export default function ClientsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isStaff = user?.role === "staff";
  const isClient = user?.role === "client";
  const canManageClients = isAdmin || isStaff;

  // Client-role users go straight to their own client dashboard.
  // We run the redirect ONCE per mount using a ref so any downstream
  // navigation (e.g. an error boundary bouncing back to '/') never creates
  // an infinite ping-pong (React error #185).
  const clientRedirectedRef = React.useRef(false);
  useEffect(() => {
    if (clientRedirectedRef.current) return;
    if (isClient && user?.clientId) {
      clientRedirectedRef.current = true;
      navigate(`/clients/${user.clientId}`, { replace: true });
    }
  }, [isClient, user, navigate]);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ["clients"],
    queryFn: api.listClients,
    enabled: !isClient,
  });

  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [deleteClient, setDeleteClient] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const createM = useMutation({
    mutationFn: api.createClient,
    onSuccess: (client) => {
      qc.invalidateQueries({ queryKey: ["clients"] });
      const gen = client.generatedCount || 0;
      toast.success(
        `Client created${gen ? ` · ${gen} compliance task${gen === 1 ? "" : "s"} generated` : ""}`
      );
      setAddOpen(false);
      navigate(`/clients/${client.id}`);
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Failed to create client"),
  });

  const updateM = useMutation({
    mutationFn: ({ id, payload }) => api.updateClient(id, payload),
    onSuccess: (c) => {
      qc.setQueryData(["clients"], (prev = []) =>
        prev.map((x) => (x.id === c.id ? c : x))
      );
      toast.success("Client updated");
      setEditClient(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const deleteM = useMutation({
    mutationFn: (id) => api.deleteClient(id),
    onSuccess: (_r, id) => {
      qc.setQueryData(["clients"], (prev = []) =>
        prev.filter((c) => c.id !== id)
      );
      toast.success("Client deleted");
      setDeleteClient(null);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const bulkDeleteM = useMutation({
    mutationFn: (ids) => api.bulkDeleteClients(ids),
    onSuccess: (r) => {
      qc.setQueryData(["clients"], (prev = []) =>
        prev.filter((c) => !r.deletedIds.includes(c.id))
      );
      const kept = r.skippedIds?.length || 0;
      toast.success(
        `${r.deletedCount} client${r.deletedCount === 1 ? "" : "s"} deleted` +
          (kept ? ` · ${kept} skipped (not yours to delete)` : "")
      );
      setSelectedIds(new Set());
      setBulkConfirmOpen(false);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bulk delete failed"),
  });

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(t) ||
        c.type.toLowerCase().includes(t) ||
        (c.pan || "").toLowerCase().includes(t) ||
        (c.gstin || "").toLowerCase().includes(t)
    );
  }, [clients, search]);

  const totals = useMemo(() => {
    const t = { clients: clients.length, pending: 0, overdue: 0, completed: 0 };
    clients.forEach((c) => {
      const s = c.stats || {};
      t.pending += s.pending || 0;
      t.overdue += s.overdue || 0;
      t.completed += s.completed || 0;
    });
    return t;
  }, [clients]);

  return (
    <div data-testid={TID.clientsPage}>
      <AppHeader
        variant="overview"
        primaryLabel={canManageClients ? "Add Client" : undefined}
        primaryTestId={TID.addClientBtn}
        onPrimary={canManageClients ? () => setAddOpen(true) : undefined}
        onOpenSettings={isAdmin ? () => setSettingsOpen(true) : undefined}
        onOpenLibrary={isAdmin ? () => setLibraryOpen(true) : undefined}
      />

      <main className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 flex flex-col gap-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <StatCard
            icon={Users}
            label="Clients"
            value={totals.clients}
            accent={{ bg: "#F1F5F9", fg: "#0F172A" }}
            loading={isLoading}
          />
          <StatCard
            icon={Clock}
            label="Pending Tasks"
            value={totals.pending}
            accent={{ bg: "#FEF3C7", fg: "#92400E" }}
            loading={isLoading}
          />
          <StatCard
            icon={AlertOctagon}
            label="Overdue"
            value={totals.overdue}
            accent={{ bg: "#FEE2E2", fg: "#991B1B" }}
            loading={isLoading}
          />
          <StatCard
            icon={Building2}
            label="Completed"
            value={totals.completed}
            accent={{ bg: "#DCFCE7", fg: "#166534" }}
            loading={isLoading}
          />
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative w-full sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients by name, PAN, GSTIN…"
              className="w-full h-9 pl-9 pr-3 rounded-lg border border-[#E2E8F0] bg-white text-sm text-[#0F172A] placeholder:text-[#94A3B8] focus:outline-none focus:ring-2 focus:ring-[#0F172A]/10"
              data-testid={TID.clientsSearch}
            />
          </div>
          <div className="flex items-center gap-2">
            {canManageClients && (
              <button
                type="button"
                onClick={() => setImportOpen(true)}
                data-testid={TID.importClientsBtn}
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-[#E2E8F0] bg-white text-sm font-medium text-[#334155] hover:border-[#CBD5E1]"
              >
                <Upload className="h-3.5 w-3.5" />
                Import
              </button>
            )}
            <div className="text-xs text-[#64748B] hidden sm:block">
              {filtered.length} of {clients.length} clients
            </div>
          </div>
        </div>

        {isLoading ? (
          <ClientsSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyClients
            hasAny={clients.length > 0}
            onAdd={() => setAddOpen(true)}
          />
        ) : (
          <div className="ab-card ab-shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="ab-table w-full text-left">
                <thead>
                  <tr>
                    <th style={{ width: 36 }} className="text-center">
                      <input
                        type="checkbox"
                        aria-label="Select all clients"
                        data-testid={TID.clientSelectAll}
                        className="h-3.5 w-3.5 accent-[#0F172A] align-middle"
                        checked={
                          filtered.length > 0 &&
                          filtered.every((c) => selectedIds.has(c.id))
                        }
                        ref={(el) => {
                          if (!el) return;
                          const some = filtered.some((c) => selectedIds.has(c.id));
                          const all =
                            filtered.length > 0 &&
                            filtered.every((c) => selectedIds.has(c.id));
                          el.indeterminate = some && !all;
                        }}
                        onChange={(e) => {
                          const next = new Set(selectedIds);
                          if (e.target.checked) {
                            filtered.forEach((c) => next.add(c.id));
                          } else {
                            filtered.forEach((c) => next.delete(c.id));
                          }
                          setSelectedIds(next);
                        }}
                      />
                    </th>
                    <th>Client</th>
                    <th>Company / Entity</th>
                    <th>Type</th>
                    <th className="text-[11px]">Client ID</th>
                    <th className="text-right">Modules</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th className="text-right">Pending</th>
                    <th>Assigned Staff</th>
                    <th style={{ width: 132 }} className="text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr
                      key={c.id}
                      className="cursor-pointer"
                      onClick={() => navigate(`/clients/${c.id}`)}
                      data-testid={TID.clientRow(c.id)}
                    >
                      <td
                        className="text-center"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          aria-label={`Select ${c.name}`}
                          data-testid={TID.clientSelect(c.id)}
                          className="h-3.5 w-3.5 accent-[#0F172A] align-middle"
                          checked={selectedIds.has(c.id)}
                          onChange={(e) => {
                            const next = new Set(selectedIds);
                            if (e.target.checked) next.add(c.id);
                            else next.delete(c.id);
                            setSelectedIds(next);
                          }}
                        />
                      </td>
                      <td>
                        <div className="flex items-center gap-2.5">
                          <div className="h-7 w-7 rounded-md bg-[#F1F5F9] text-[#334155] flex items-center justify-center text-xs font-semibold shrink-0">
                            {c.name.slice(0, 1).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-medium text-[#0F172A]">
                              {c.contactName || c.name}
                            </div>
                            <div className="text-[11px] text-[#64748B]">
                              {c.contactEmail || c.pan || "—"}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className="text-[13px] text-[#0F172A]">
                          {c.name}
                        </div>
                        <div className="text-[11px] text-[#64748B]">
                          {c.gstin || c.pan || "—"}
                        </div>
                      </td>
                      <td>
                        <span className="text-xs text-[#334155] bg-[#F1F5F9] rounded px-1.5 py-0.5 font-medium">
                          {companyTypeLabel(c.type)}
                        </span>
                      </td>
                      <td>
                        <span className="text-[11px] font-mono text-[#64748B]" title={c.id}>
                          {(c.id || "").slice(0, 8)}
                        </span>
                      </td>
                      <td className="text-right tabular-nums text-[#64748B]">
                        {c.activeModulesCount ?? 0}
                      </td>
                      <td>
                        <AccountStatusBadge status={c.accountStatus || "pending_activation"} />
                      </td>
                      <td className="text-[11px] text-[#64748B] whitespace-nowrap">
                        {c.lastLoginAt ? formatCreatedAt(c.lastLoginAt) : (
                          <span className="text-[#94A3B8]">Never</span>
                        )}
                      </td>
                      <td className="text-right tabular-nums font-medium">
                        {c.stats?.pending ?? 0}
                      </td>
                      <td className="text-[12px] text-[#334155]">
                        {c.assignedStaffName || (
                          <span className="text-[#94A3B8]">Unassigned</span>
                        )}
                      </td>
                      <td className="text-right whitespace-nowrap">
                        <div
                          className="inline-flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => navigate(`/clients/${c.id}`)}
                            data-testid={TID.clientOpen(c.id)}
                            className="h-7 px-2 inline-flex items-center gap-1 rounded-md border border-[#E2E8F0] bg-white text-xs font-medium text-[#334155] hover:bg-[#F8FAFC]"
                          >
                            Open
                            <ArrowRight className="h-3 w-3" />
                          </button>
                          {isAdmin && (
                            <>
                              <button
                                type="button"
                                onClick={() => setEditClient(c)}
                                data-testid={TID.clientEdit(c.id)}
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#F8FAFC]"
                                title="Edit"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                onClick={() => setDeleteClient(c)}
                                data-testid={TID.clientDelete(c.id)}
                                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-[#E2E8F0] text-[#334155] hover:bg-[#FEF2F2] hover:text-[#DC2626] hover:border-[#FECACA]"
                                title="Delete"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      <ClientFormModal
        mode="create"
        open={addOpen}
        onOpenChange={setAddOpen}
        onSubmit={(payload) => createM.mutate(payload)}
        submitting={createM.isPending}
      />
      <ClientFormModal
        mode="edit"
        open={!!editClient}
        onOpenChange={(o) => !o && setEditClient(null)}
        initial={editClient}
        onSubmit={(payload) =>
          editClient && updateM.mutate({ id: editClient.id, payload })
        }
        submitting={updateM.isPending}
      />
      <DeleteConfirmDialog
        open={!!deleteClient}
        onOpenChange={(o) => !o && setDeleteClient(null)}
        task={
          deleteClient
            ? {
                title: `${deleteClient.name} — all ${
                  deleteClient.stats?.total || 0
                } tasks will be deleted`,
              }
            : null
        }
        onConfirm={() => deleteClient && deleteM.mutate(deleteClient.id)}
        submitting={deleteM.isPending}
      />
      <SettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
      <ImportClientsModal open={importOpen} onOpenChange={setImportOpen} />
      <MasterLibraryModal open={libraryOpen} onOpenChange={setLibraryOpen} />

      <BulkActionsBar
        count={selectedIds.size}
        entityLabel="client"
        onClear={() => setSelectedIds(new Set())}
        onDelete={() => setBulkConfirmOpen(true)}
        submitting={bulkDeleteM.isPending}
        testId={TID.clientsBulkBar}
        deleteTestId={TID.clientsBulkDelete}
      />
      <BulkDeleteConfirmDialog
        open={bulkConfirmOpen}
        onOpenChange={(o) => !bulkDeleteM.isPending && setBulkConfirmOpen(o)}
        count={selectedIds.size}
        entityLabel="client"
        cascadeNote="All tasks and rule overrides for the selected clients will also be removed."
        onConfirm={() => bulkDeleteM.mutate(Array.from(selectedIds))}
        submitting={bulkDeleteM.isPending}
      />
    </div>
  );
}

function formatCreatedAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function StatCard({ icon: Icon, label, value, accent, loading }) {
  return (
    <div className="ab-card ab-shadow-sm px-4 py-3.5 flex items-center gap-3">
      <div
        className="h-9 w-9 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: accent.bg, color: accent.fg }}
      >
        <Icon className="h-4 w-4" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider text-[#64748B] font-medium">
          {label}
        </div>
        <div className="text-xl font-semibold text-[#0F172A] leading-tight mt-0.5 tabular-nums">
          {loading ? <Skeleton className="h-6 w-10" /> : value}
        </div>
      </div>
    </div>
  );
}

function ClientsSkeleton() {
  return (
    <div className="ab-card ab-shadow-sm overflow-hidden">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="px-4 py-3.5 border-b border-[#E2E8F0] flex items-center gap-6 last:border-b-0"
        >
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-3.5 w-56" />
          <Skeleton className="h-3.5 w-24 ml-auto" />
          <Skeleton className="h-3.5 w-16" />
          <Skeleton className="h-3.5 w-24" />
        </div>
      ))}
    </div>
  );
}

function EmptyClients({ hasAny, onAdd }) {
  return (
    <div className="ab-card ab-shadow-sm px-6 py-12 flex flex-col items-center text-center">
      <div className="h-11 w-11 rounded-lg bg-[#F1F5F9] flex items-center justify-center mb-4">
        <Users className="h-5 w-5 text-[#64748B]" />
      </div>
      <h3 className="text-sm font-semibold text-[#0F172A]">
        {hasAny ? "No matching clients" : "No clients yet"}
      </h3>
      <p className="mt-1 text-xs text-[#64748B] max-w-sm">
        {hasAny
          ? "Try a different search term."
          : "Add your first client. Pick a company type and we'll instantly load their applicable statutory compliances."}
      </p>
      {!hasAny && (
        <button
          type="button"
          onClick={onAdd}
          data-testid="add-first-client-btn"
          className={cn(
            "mt-4 inline-flex items-center gap-2 rounded-md bg-[#0F172A] text-white h-9 px-3.5 text-sm font-medium hover:bg-[#1E293B]"
          )}
        >
          Add client
        </button>
      )}
    </div>
  );
}
